import React, { useState } from 'react';
import { TextField, IconButton, Snackbar, InputAdornment } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';

/**
 * A reusable component that displays a link in a standard MUI TextField.
 * It provides buttons to copy the link and open it in a new tab.
 *
 * @param {object} props
 * @param {string} props.link The URL to be displayed and copied.
 */
const CopyableLink = ({ link }) => {
    // State to manage the visual feedback (changing icon to a checkmark)
    const [copied, setCopied] = useState(false);
    // State to control the visibility of the success message
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    /**
     * Handles the copy-to-clipboard functionality.
     * Uses the document.execCommand('copy') for broader compatibility,
     * especially in sandboxed environments like iframes.
     */
    const handleCopy = () => {
        // Create a temporary textarea element to hold the link text
        const textArea = document.createElement('textarea');
        textArea.value = link;
        
        // Style it to be hidden from view
        textArea.style.position = 'fixed';
        textArea.style.top = 0;
        textArea.style.left = 0;
        textArea.style.opacity = 0;

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            // Execute the copy command
            const successful = document.execCommand('copy');
            if (successful) {
                // Provide visual feedback on success
                setCopied(true);
                setSnackbarOpen(true);
                // Reset the icon feedback after 2 seconds
                setTimeout(() => {
                    setCopied(false);
                }, 2000);
            }
        } catch (err) {
            console.error('Fallback: Oops, unable to copy', err);
        }

        // Clean up and remove the temporary textarea
        document.body.removeChild(textArea);
    };

    /**
     * Handles opening the link in a new tab
     */
    const handleOpenLink = () => {
        window.open(link, '_blank', 'noopener,noreferrer');
    };

    /**
     * Closes the snackbar message.
     */
    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    /**
     * Handles clicking on the text field to open the link
     */
    const handleFieldClick = () => {
        window.open(link, '_blank', 'noopener,noreferrer');
    };

    return (
        <React.Fragment>
            <TextField
                fullWidth
                variant="outlined"
                value={link}
                onClick={handleFieldClick}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                aria-label="open link in new tab"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent field click when opening link
                                    handleOpenLink();
                                }}
                                edge="end"
                                
                                sx={{ marginRight: 0.5 }}
                            >
                                <OpenInNewIcon fontSize="small" />
                            </IconButton>
                            {/* <IconButton
                                aria-label="copy link to clipboard"
                                onClick={(e) => {
                                    e.stopPropagation(); // Prevent field click when copying
                                    handleCopy();
                                }}
                                edge="end"
                            >
                                {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
                            </IconButton> */}
                        </InputAdornment>
                    ),
                }}
                sx={{
                    '& .MuiOutlinedInput-input': {
                        cursor: 'pointer',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                    },
                    '& .MuiOutlinedInput-root': {
                        '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'primary.main',
                            },
                        },
                    },
                }}
            />
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                message="Ctrl+C"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </React.Fragment>
    );
};

export default CopyableLink;